import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SEOHead, FAQSchema } from '../components/SEO';
import { SEO_KEYWORDS } from '../components/SEO';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      category: "General",
      questions: [
        {
          q: "What is HumanOnRent.AI?",
          a: "HumanOnRent.AI is India's first AI-driven platform connecting people who need professional rental services with verified providers. Whether you need someone for events, travel, medical visits, or simply great company, we've got you covered."
        },
        {
          q: "Is this service legal and safe?",
          a: "Absolutely! Our platform facilitates purely platonic professional services. All providers undergo thorough background verification, and we have strict code of conduct policies. We also provide SOS emergency features for user safety."
        },
        {
          q: "What services are available?",
          a: "We offer 20+ service categories including Movies, Shopping, Travel, Medical Help, Elder Care, Business Events, Dining, Parties, and more. Check our Services page for the complete list."
        }
      ]
    },
    {
      category: "For Hirers",
      questions: [
        {
          q: "How do I book a provider?",
          a: "1. Choose your service category\n2. Select date and number of hours\n3. Make payment (service fee x hours)\n4. Search for available providers in your area\n5. Send booking request\n6. Wait for acceptance and connect!"
        },
        {
          q: "What if my request is rejected?",
          a: "If a provider rejects your request, you can immediately search for other available providers and send new requests. The rejection reason will be displayed for your reference."
        },
        {
          q: "How much does it cost?",
          a: "Costs vary based on the provider's hourly rate. You pay: (Hourly Rate × Hours) + ₹99 service access fee per category. Average hourly rates range from ₹200 to ₹1000 depending on the service and provider."
        },
        {
          q: "Is my payment secure?",
          a: "Yes, all payments are processed through secure payment gateways. Your financial information is never stored on our servers."
        }
      ]
    },
    {
      category: "For Providers",
      questions: [
        {
          q: "How do I become a provider?",
          a: "1. Sign up with your mobile number\n2. Complete your profile with services you offer\n3. Purchase a membership plan\n4. Get verified by our team\n5. Start receiving booking requests!"
        },
        {
          q: "What are the membership plans?",
          a: "We offer three plans:\n• 6 Months: ₹249 + 18% GST = ₹294\n• 5 Years: ₹499 + 18% GST = ₹589\n• Lifetime: ₹999 + 18% GST = ₹1179"
        },
        {
          q: "How much do I earn?",
          a: "You receive 80% of the booking amount. For example, if a hirer pays ₹1000 for your services, you earn ₹800. The platform retains 20% as commission."
        },
        {
          q: "How do I withdraw my earnings?",
          a: "Go to your dashboard's Earnings section, click 'Withdraw', enter the amount (minimum ₹100), and provide your UPI ID or bank details. Withdrawals are processed within 24-48 hours."
        },
        {
          q: "Can I reject a booking?",
          a: "Yes, you can accept or reject any booking request. If rejecting, you must provide a valid reason which will be shown to the hirer."
        }
      ]
    },
    {
      category: "Safety & Support",
      questions: [
        {
          q: "What is the SOS feature?",
          a: "The SOS button in your dashboard sends an immediate emergency alert to our support team. We will contact you and relevant authorities if needed. Use it in any uncomfortable or unsafe situation."
        },
        {
          q: "How are providers verified?",
          a: "All providers undergo:\n• ID verification (Aadhaar/PAN)\n• Phone number verification\n• Background check\n• Profile review by our team"
        },
        {
          q: "What if I have a complaint?",
          a: "Contact us immediately through the Help Center or email support@humanonrent.com. We take all complaints seriously and investigate thoroughly."
        }
      ]
    },
    {
      category: "Payments & Refunds",
      questions: [
        {
          q: "What payment methods are accepted?",
          a: "We accept UPI, Credit/Debit Cards, Net Banking, and popular wallets like Paytm and PhonePe."
        },
        {
          q: "Are memberships refundable?",
          a: "No, membership fees are non-refundable under any circumstances. Please choose your plan carefully before purchasing."
        },
        {
          q: "What if a provider cancels after I've paid?",
          a: "If a provider cancels, you can either book another provider or request a full refund to your original payment method."
        }
      ]
    }
  ];

  const allFaqsFlat = faqs.flatMap(cat => cat.questions.map(q => ({ question: q.q, answer: q.a })));

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <SEOHead
        title="FAQ - Human on Rent Questions Answered | HumanOnRent.AI"
        description="Get answers to frequently asked questions about HumanOnRent.AI. How to rent a human, pricing, safety, provider earnings, membership plans, and more."
        keywords={SEO_KEYWORDS.faq}
        url="https://humanonrent.ai/faq"
      />
      <FAQSchema faqs={allFaqsFlat} />
      <span className="sr-only-seo">human on rent faq, how to rent a human, humanonrent pricing</span>

      <Header />

      {/* Content */}
      <main className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Frequently Asked <span className="font-medium">Questions</span>
            </h1>
            <p className="text-xl text-gray-600">
              Find answers to common questions about HumanOnRent.AI
            </p>
          </div>

          {faqs.map((category, catIdx) => (
            <div key={category.category} className="mb-12">
              <h2 className="text-2xl font-medium text-gray-900 mb-6">{category.category}</h2>
              <div className="space-y-4">
                {category.questions.map((faq, idx) => {
                  const globalIdx = catIdx * 100 + idx;
                  const isOpen = openIndex === globalIdx;
                  return (
                    <div key={faq.q} className="premium-card overflow-hidden">
                      <button
                        onClick={() => setOpenIndex(isOpen ? -1 : globalIdx)}
                        className="w-full p-6 flex items-center justify-between text-left"
                      >
                        <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-6">
                          <p className="text-gray-600 whitespace-pre-line leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="text-center mt-16">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Link to="/contact" className="btn-premium inline-block">
              Contact Support
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
